import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { DataGrid, DetailPane, SplitView, StatusBar, type GridColumn } from '@/components/desktop';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import { ErrorNote, money, shortDate } from '@/components/ui';
import { formatCount } from '@/format';
import { FREQUENCY_TEXT, hrGet, periodLabel } from '@/hooks/hr';
import { useShellNav, type ScreenIntent } from '@/stores/shellNav';
import './hr-admin.css';

interface RecentRun {
  id: string;
  documentNo: string;
  payGroup: string;
  periodStart: string | null;
  periodEnd: string | null;
  farm: string;
  stage: string;
  headcount: number;
  totalNet?: string | null;
}

interface Dashboard {
  today: string;
  headcount: { monthly: number; daily: number; suspended: number };
  presentToday: number;
  onLeaveToday: number;
  attendanceNotMarked: {
    date: string;
    farms: Array<{ farmId: string; farm: string; staff: number; marked: number }>;
  };
  /** outstandingAmount is left out for anyone who cannot see pay. */
  advances: { open: number; outstandingAmount?: string | null };
  waiting: {
    payChanges: number;
    leave: number;
    advances: number;
    advancesToPayOut: number;
    payRunsToApprove: number;
    payRunsToPost: number;
    settlements: number;
  };
  attention: {
    probationEnding: number;
    paymentDetailsMissing: number;
    cnicMissing: number;
    /** null when you cannot see personal details. */
    cnicExpiring: number | null;
  };
  recentRuns: RecentRun[];
}

interface Attention {
  key: string;
  text: string;
  icon: IconKind;
  screen: string;
  intent?: ScreenIntent;
}

function go(screen: string, intent?: ScreenIntent) {
  useShellNav.getState().navigate(screen, intent);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** What is waiting, in plain words, with the screen each one is decided on. */
const WAITING: Array<{ key: keyof Dashboard['waiting']; text: (n: number) => string; icon: IconKind; screen: string }> = [
  { key: 'payChanges', text: (n) => `${plural(n, 'pay change is', 'pay changes are')} waiting for a decision`, icon: 'payroll', screen: 'hr-emp' },
  { key: 'leave', text: (n) => `${plural(n, 'leave request is', 'leave requests are')} waiting for a decision`, icon: 'leave', screen: 'hr-leave' },
  { key: 'advances', text: (n) => `${plural(n, 'advance or loan is', 'advances and loans are')} waiting for a decision`, icon: 'advance', screen: 'hr-adv' },
  { key: 'advancesToPayOut', text: (n) => `${plural(n, 'advance or loan is', 'advances and loans are')} approved and still to be paid out`, icon: 'cash', screen: 'hr-adv' },
  { key: 'payRunsToApprove', text: (n) => `${plural(n, 'pay run is', 'pay runs are')} waiting for approval`, icon: 'payroll', screen: 'hr-pay' },
  { key: 'payRunsToPost', text: (n) => `${plural(n, 'pay run is', 'pay runs are')} approved and still to be posted`, icon: 'post', screen: 'hr-pay' },
  { key: 'settlements', text: (n) => `${plural(n, 'final settlement is', 'final settlements are')} waiting to be approved or posted`, icon: 'settlement', screen: 'hr-settle' },
];

const MAX_ATTENTION = 20;

function Tile({
  label,
  icon,
  value,
  note,
  tone,
  title,
  onClick,
}: {
  label: string;
  icon: IconKind;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'warn' | 'alert' | undefined;
  title?: string | undefined;
  onClick?: (() => void) | undefined;
}) {
  const className = `worktile${tone ? ` worktile--${tone}` : ''}${onClick ? ' worktile--action' : ''}`;
  const body = (
    <>
      <span className="worktile__label withicon">
        <AppIcon kind={icon} size={14} />
        {label}
      </span>
      <strong>{value}</strong>
      {note && <span className="hrtile__note">{note}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className={className} title={title} onClick={onClick}>
      {body}
    </button>
  ) : (
    <span className={className} title={title}>
      {body}
    </span>
  );
}

/**
 * HR at a glance: how many people, who is in today, what is owed, and what is
 * waiting for someone to decide. Every item in "Needs attention" opens the
 * screen where it is dealt with. Covers every farm you can see — the server
 * works these figures out across your farms, not for the one in the top bar.
 */
export function HrDashboard() {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const dash = useQuery({
    queryKey: ['hr', 'dashboard'],
    queryFn: () => hrGet<Dashboard>('/api/v1/hr/dashboard'),
  });
  const d = dash.data;

  const waitingItems = d ? WAITING.filter((w) => d.waiting[w.key] > 0) : [];
  const waitingTotal = d ? WAITING.reduce((t, w) => t + d.waiting[w.key], 0) : 0;
  const breakdown = waitingItems.map((w) => w.text(d!.waiting[w.key])).join('\n');
  const notMarked = d?.attendanceNotMarked.farms ?? [];
  const latestRun = d?.recentRuns[0];

  const attention: Attention[] = d
    ? [
        ...waitingItems.map((w) => ({ key: w.key, text: w.text(d.waiting[w.key]), icon: w.icon, screen: w.screen })),
        ...notMarked.map((f) => ({
          key: `att-${f.farmId}`,
          text: `${f.farm}: attendance for ${shortDate(d.attendanceNotMarked.date)} not marked (${f.marked} of ${f.staff} done)`,
          icon: 'attendance' as const,
          screen: 'hr-att',
          intent: { farmId: f.farmId },
        })),
        ...(d.attention.probationEnding > 0
          ? [{ key: 'probation', text: `${plural(d.attention.probationEnding, 'person finishes', 'people finish')} probation in the next 30 days`, icon: 'employee' as const, screen: 'hr-emp' }]
          : []),
        ...(d.attention.paymentDetailsMissing > 0
          ? [{ key: 'paydetails', text: `${plural(d.attention.paymentDetailsMissing, 'person is', 'people are')} paid by bank or wallet but the account details are missing`, icon: 'bank' as const, screen: 'hr-emp' }]
          : []),
        ...(d.attention.cnicMissing > 0
          ? [{ key: 'cnicmissing', text: `${plural(d.attention.cnicMissing, 'person has', 'people have')} no CNIC on file`, icon: 'designation' as const, screen: 'hr-emp' }]
          : []),
        ...(d.attention.cnicExpiring !== null && d.attention.cnicExpiring > 0
          ? [{ key: 'cnicexpiring', text: `${plural(d.attention.cnicExpiring, 'CNIC has', 'CNICs have')} expired or will expire in the next 60 days`, icon: 'attention' as const, screen: 'hr-emp' }]
          : []),
      ]
    : [];

  const columns: Array<GridColumn<RecentRun>> = [
    {
      key: 'doc',
      header: 'Number',
      width: 150,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind="payroll" size={14} />
          <span className="mono">{r.documentNo}</span>
        </span>
      ),
    },
    { key: 'period', header: 'Period', width: 170, render: (r) => (r.periodStart && r.periodEnd ? periodLabel(r.periodStart, r.periodEnd) : '—') },
    { key: 'group', header: 'Paid', width: 100, render: (r) => FREQUENCY_TEXT[r.payGroup] ?? r.payGroup },
    { key: 'farm', header: 'Farm', width: 80, render: (r) => <span className="dim">{r.farm === 'All' ? 'All farms' : r.farm}</span> },
    { key: 'people', header: 'People', align: 'end', width: 80, render: (r) => formatCount(r.headcount) },
    { key: 'net', header: 'Net pay', align: 'end', width: 120, render: (r) => money(r.totalNet) },
    { key: 'stage', header: 'Stage', width: 180, render: (r) => <StatePill state={r.stage} /> },
  ];

  const people = d ? d.headcount.monthly + d.headcount.daily : 0;

  return (
    <div className="workbench workbench--cards">
      <div className="workbench__tiles">
        <Tile
          label="People (monthly · daily)"
          icon="people"
          value={d ? `${formatCount(d.headcount.monthly)} · ${formatCount(d.headcount.daily)}` : '…'}
          note={d && d.headcount.suspended > 0 ? `${d.headcount.suspended} suspended` : undefined}
        />
        <Tile label="Present today" icon="attendance" value={d ? formatCount(d.presentToday) : '…'} />
        <Tile label="On leave today" icon="leave" value={d ? formatCount(d.onLeaveToday) : '…'} />
        <Tile
          label="Farms not marked yesterday"
          icon="attendance"
          value={d ? formatCount(notMarked.length) : '…'}
          tone={notMarked.length > 0 ? 'alert' : undefined}
          title={notMarked.length > 0 ? 'Open Attendance' : undefined}
          onClick={notMarked.length > 0 ? () => go('hr-att') : undefined}
        />
        <Tile
          label="Owed on advances & loans"
          icon="advance"
          value={d?.advances.outstandingAmount ? money(d.advances.outstandingAmount) : '—'}
          note={d ? `${formatCount(d.advances.open)} still being repaid` : undefined}
          onClick={() => go('hr-adv')}
        />
        <Tile
          label="Waiting for a decision"
          icon="statusPending"
          value={d ? formatCount(waitingTotal) : '…'}
          tone={waitingTotal > 0 ? 'warn' : undefined}
          title={breakdown || 'Nothing is waiting'}
          onClick={waitingItems[0] ? () => go(waitingItems[0]!.screen) : undefined}
        />
        <Tile
          label="This month's pay"
          icon="payroll"
          value={latestRun ? stateLabel(latestRun.stage) : d ? 'Not started' : '…'}
          note={
            latestRun && latestRun.periodStart && latestRun.periodEnd
              ? `${latestRun.documentNo} · ${periodLabel(latestRun.periodStart, latestRun.periodEnd)}`
              : undefined
          }
          onClick={() => (latestRun ? go('hr-pay', { recordId: latestRun.id }) : go('hr-pay'))}
        />
      </div>

      {dash.error && <ErrorNote message={dash.error.message} />}

      <SplitView
        id="hr-dashboard"
        initial={760}
        firstLabel="recent pay runs"
        first={
          <div className="workbench__main">
            <h2 className="formsection">Recent pay runs</h2>
            <DataGrid
              id="hr-dashboard-runs"
              label="Recent pay runs"
              columns={columns}
              rows={d?.recentRuns ?? []}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(r) => go('hr-pay', { recordId: r.id })}
              actions={(r) => [{ label: 'Open pay run', icon: 'payroll', shortcut: 'Enter', onSelect: () => go('hr-pay', { recordId: r.id }) }]}
              loading={dash.isPending}
              empty="No pay runs in the last two months."
            />
          </div>
        }
        second={
          <DetailPane
            tabs={[
              {
                id: 'attention',
                label: 'Needs attention',
                icon: 'attention',
                content: dash.isPending ? (
                  <p className="muted">Loading…</p>
                ) : attention.length === 0 ? (
                  <p className="muted">Nothing needs attention. Nothing is waiting, and every farm marked attendance yesterday.</p>
                ) : (
                  <ul className="attentionlist">
                    {attention.slice(0, MAX_ATTENTION).map((a) => (
                      <li key={a.key}>
                        <button type="button" className="linkbutton withicon" onClick={() => go(a.screen, a.intent)}>
                          <AppIcon kind={a.icon} />
                          {a.text}
                        </button>
                      </li>
                    ))}
                    {attention.length > MAX_ATTENTION && <li className="muted">and {attention.length - MAX_ATTENTION} more</li>}
                  </ul>
                ),
              },
            ]}
            activeTab="attention"
            onTabChange={() => {}}
          />
        }
      />

      <StatusBar>
        <span>All farms you can see</span>
        {d && <span>Today {shortDate(d.today)}</span>}
        {d && <span>{plural(people, 'person', 'people')} working</span>}
        {waitingTotal > 0 && <span>{waitingTotal} waiting for a decision</span>}
      </StatusBar>
    </div>
  );
}
